import { useState, useEffect } from "react";

export const useDeviceDetection = () => {
  const [deviceInfo, setDeviceInfo] = useState({
    deviceName: "Unknown Device",
    deviceType: "desktop", // desktop, mobile, tablet
    os: "Unknown OS",
    browser: "Unknown Browser",
    isMobile: false,
    isTablet: false,
    isDesktop: true,
  });

  useEffect(() => {
    const detectDevice = () => {
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;

      let deviceName = "Unknown Device";
      let deviceType = "desktop";
      let os = "Unknown OS";
      let browser = "Unknown Browser";

      // Detect OS
      if (/Mac|iPhone|iPod|iPad/.test(userAgent)) {
        if (/iPhone/.test(userAgent)) {
          os = "iOS";
          deviceName = getIPhoneModel(userAgent);
          deviceType = "mobile";
        } else if (/iPad/.test(userAgent)) {
          os = "iPadOS";
          deviceName = getIPadModel(userAgent);
          deviceType = "tablet";
        } else if (/Mac/.test(userAgent)) {
          os = "macOS";
          deviceName = getMacModel(userAgent, platform);
          deviceType = "desktop";
        }
      } else if (/Android/.test(userAgent)) {
        os = "Android";
        deviceName = getAndroidModel(userAgent);
        deviceType = /Mobile/.test(userAgent) ? "mobile" : "tablet";
      } else if (/Windows/.test(userAgent)) {
        os = "Windows";
        deviceName = getWindowsDevice(userAgent);
        deviceType = "desktop";
      } else if (/Linux/.test(userAgent)) {
        os = "Linux";
        deviceName = "Linux Device";
        deviceType = "desktop";
      }

      // Detect Browser
      if (/Chrome/.test(userAgent) && !/Edge/.test(userAgent)) {
        browser = "Chrome";
      } else if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) {
        browser = "Safari";
      } else if (/Firefox/.test(userAgent)) {
        browser = "Firefox";
      } else if (/Edge/.test(userAgent)) {
        browser = "Edge";
      }

      setDeviceInfo({
        deviceName,
        deviceType,
        os,
        browser,
        isMobile: deviceType === "mobile",
        isTablet: deviceType === "tablet",
        isDesktop: deviceType === "desktop",
      });
    };

    detectDevice();
  }, []);

  // Helper functions for device detection
  const getIPhoneModel = (userAgent) => {
    if (/iPhone/.test(userAgent)) {
      // Try to detect iPhone model based on screen resolution or other indicators
      const screenHeight = window.screen.height;
      const screenWidth = window.screen.width;
      const pixelRatio = window.devicePixelRatio;

      // iPhone model detection based on screen dimensions
      if (screenHeight === 896 && screenWidth === 414 && pixelRatio === 2) {
        return "iPhone 11";
      } else if (
        screenHeight === 926 &&
        screenWidth === 428 &&
        pixelRatio === 3
      ) {
        return "iPhone 14 Pro Max";
      } else if (
        screenHeight === 852 &&
        screenWidth === 393 &&
        pixelRatio === 3
      ) {
        return "iPhone 14 Pro";
      } else if (
        screenHeight === 844 &&
        screenWidth === 390 &&
        pixelRatio === 3
      ) {
        return "iPhone 12/13/14";
      } else if (
        screenHeight === 812 &&
        screenWidth === 375 &&
        pixelRatio === 3
      ) {
        return "iPhone X/XS/11 Pro";
      } else if (
        screenHeight === 736 &&
        screenWidth === 414 &&
        pixelRatio === 3
      ) {
        return "iPhone 6/7/8 Plus";
      } else if (
        screenHeight === 667 &&
        screenWidth === 375 &&
        pixelRatio === 2
      ) {
        return "iPhone 6/7/8";
      }
      return "iPhone";
    }
    return "iPhone";
  };

  const getIPadModel = (userAgent) => {
    const screenHeight = window.screen.height;
    const screenWidth = window.screen.width;

    if (screenHeight === 1366 && screenWidth === 1024) {
      return 'iPad Pro 12.9"';
    } else if (screenHeight === 1194 && screenWidth === 834) {
      return 'iPad Pro 11"';
    } else if (screenHeight === 1080 && screenWidth === 810) {
      return "iPad Air";
    } else if (screenHeight === 1024 && screenWidth === 768) {
      return "iPad";
    }
    return "iPad";
  };

  const getMacModel = (userAgent, platform) => {
    // Try to detect Mac model
    if (/Intel/.test(userAgent) || /Intel/.test(platform)) {
      return "Mac (Intel)";
    } else if (/ARM/.test(userAgent) || /Apple/.test(platform)) {
      return "MacBook (Apple Silicon)";
    }

    // Fallback based on platform
    if (/MacIntel/.test(platform)) {
      // Could be MacBook Pro, MacBook Air, iMac, etc.
      // Without more specific info, we'll use generic names
      return "MacBook";
    }

    return "Mac";
  };

  const getAndroidModel = (userAgent) => {
    // Try to extract Android device model
    const androidMatch = userAgent.match(/Android.*?;\s*(.*?)\s*\)/);
    if (androidMatch && androidMatch[1]) {
      let model = androidMatch[1];

      // Clean up common patterns
      model = model.replace(/Build.*/, "").trim();
      model = model.replace(/wv.*/, "").trim();

      // Common Android device names
      if (/Samsung|SM-/.test(model)) {
        if (/Galaxy/.test(model)) {
          return model;
        }
        return `Samsung ${model}`;
      } else if (/Pixel/.test(model)) {
        return `Google ${model}`;
      } else if (/OnePlus/.test(model)) {
        return model;
      } else if (/Xiaomi|Mi/.test(model)) {
        return model.includes("Xiaomi") ? model : `Xiaomi ${model}`;
      }

      return model || "Android Device";
    }
    return "Android Device";
  };

  const getWindowsDevice = (userAgent) => {
    if (/Windows NT 10/.test(userAgent)) {
      return "Windows 10/11 PC";
    } else if (/Windows NT 6.3/.test(userAgent)) {
      return "Windows 8.1 PC";
    } else if (/Windows NT 6.1/.test(userAgent)) {
      return "Windows 7 PC";
    }
    return "Windows PC";
  };

  return deviceInfo;
};
